// Copyright 2026 Hardfin, Inc.

/**
 * Hook for searching brands with debouncing.
 * Provides typeahead functionality for company name suggestions.
 */
import { minQueryLength, searchBrands } from '@/brand/api/search-brands';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

/**
 * Default debounce delay in milliseconds.
 */
const defaultDelay = 300;

/**
 * Hook for searching brands with debouncing.
 * Returns brand suggestions based on user input.
 * Only queries the backend when input is 2+ characters.
 *
 * @param delay - Debounce delay in milliseconds (default: 300)
 */
export function useBrandSearch(delay = defaultDelay) {
  const [input, setInput] = useState('');

  const debouncedSearch = useMemo(() => debounce(setInput, delay), [delay]);

  // Cancel debounced function on unmount to prevent state updates
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const isEnabled = input.length >= minQueryLength;

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['brand', 'search', input],
    queryFn: () => searchBrands(input),
    enabled: isEnabled,
  });

  const reset = () => {
    setInput('');
  };

  const search = (value: string) => {
    if (value === '') {
      reset();
    } else {
      debouncedSearch(value);
    }
  };

  // Only return results if query is enabled (input meets minimum length)
  const results = isEnabled ? (query.data ?? []) : [];

  return {
    reset,
    search,
    results,
    isSearching: query.isFetching,
  };
}

// Re-export BrandResult for convenience
export type { BrandResult } from '@/brand/api/search-brands';
